import React, { useState } from "react";
import SelectField from "../../Utils/SelectField";

const LIST_TYPE_OPTIONS = [
  { label: "Open List", value: "open" },
  { label: "Single select", value: "single" },
  { label: "Multi select", value: "multi" },
];

const styles = `
.list-configuration {
  width: 101.2%;
  margin: 0px 0 24px;
  padding: 16px;
  border: 1px solid #c5dcf5;
  border-radius: 4px;
  background: #fbfcfe;
  box-sizing: border-box;
}

.list-configuration * {
  box-sizing: border-box;
}

.list-configuration .lc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.list-configuration .lc-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  color: #1b2a4a;
  font-size: 16px;
  font-weight: 600;
}

.list-configuration .lc-icon {
  display: inline-flex;
  width: 20px;
  height: 20px;
  color: #3b78d8;
}

.list-configuration .lc-icon svg {
  width: 100%;
  height: 100%;
}

.list-configuration .lc-count {
  padding: 8px 16px;
  border-radius: 24px;
  background: #e8f1fd;
  color: #1376D3;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
  font-weight: normal;
}

.list-configuration .lc-grid {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: 24px;
  align-items: start;
}

.list-configuration .lc-field {
  min-width: 0;
}

/* ---------- Values editor (single bordered box) ---------- */
.list-configuration .lc-editor {
  border: 1px solid #cdd5e0;
  border-radius: 5px;
  background: #fff;
  overflow: hidden;
}

.list-configuration .lc-empty {
  min-height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  text-align: center;
  color: #6b7686;
  font-size: 14px;
}

.list-configuration .lc-empty small {
  color: #9aa4b5;
  font-size: 13px;
}

.list-configuration .lc-values {
  max-height: 240px;
  overflow-y: auto;
}

.list-configuration .lc-value-row {
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 20px;
  border-bottom: 1px solid #d7dee8;
  color: #1b2a4a;
  font-size: 14px;
  overflow-wrap: anywhere;
}

.list-configuration .lc-remove {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: #1b2a4a;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}

.list-configuration .lc-remove:hover {
  color: #dc3545;
}

.list-configuration .lc-add-row {
  display: flex;
  align-items: stretch;
  border-top: 2px solid #d7dee8;
  background: #fff;
}

.list-configuration .lc-values:empty + .lc-add-row {
  border-top: none;
}

.list-configuration .lc-input {
  flex: 1;
  width: 100%;
  min-width: 0;
  min-height: 40px;
  padding: 10px 20px;
  border: none;
  border-radius: 0;
  outline: none;
  box-shadow: none;
  background: transparent;
  color: #1b2a4a;
  font-size: 14px;
}

.list-configuration .lc-input::placeholder {
  color: #9aa4b5;
}

.list-configuration .lc-add-button {
  flex-shrink: 0;
  width: 62px;
  border: none;
  border-left: 1px solid #d7dee8;
  background: #fff;
  color: #1b2a4a;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.list-configuration .lc-add-button:hover {
  background: #f0f7ff;
  color: #0878ed;
}

.list-configuration .lc-error {
  margin-top: 6px;
  color: #dc3545;
  font-size: 13px;
}

@media (max-width: 767px) {
  .list-configuration {
    padding: 16px;
    margin: 12px 0 20px;
  }

  .list-configuration .lc-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }

  .list-configuration .lc-title {
    font-size: 18px;
    gap: 10px;
  }

  .list-configuration .lc-icon {
    width: 22px;
    height: 22px;
  }

  .list-configuration .lc-count {
    padding: 6px 12px;
    font-size: 13px;
  }

  .list-configuration .lc-empty {
    min-height: 150px;
    font-size: 15px;
  }

  .list-configuration .lc-value-row,
  .list-configuration .lc-input {
    min-height: 48px;
    padding-left: 14px;
    padding-right: 14px;
    font-size: 15px;
  }

  .list-configuration .lc-add-button {
    width: 52px;
    font-size: 24px;
  }
}
`;

const ListIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="4" cy="6" r="1" fill="currentColor" />
    <circle cx="4" cy="12" r="1" fill="currentColor" />
    <circle cx="4" cy="18" r="1" fill="currentColor" />
    <line x1="9" y1="6" x2="21" y2="6" />
    <line x1="9" y1="12" x2="21" y2="12" />
    <line x1="9" y1="18" x2="21" y2="18" />
  </svg>
);

export default function ListConfiguration({
  values,
  setFieldValue,
  errors,
  touched,
}) {
  const [newValue, setNewValue] = useState("");

  const listType = values.listType?.value || values.listType || "open";

  const selectedListType =
    LIST_TYPE_OPTIONS.find((option) => option.value === listType) ||
    LIST_TYPE_OPTIONS[0];

  const listValues = Array.isArray(values.attributeValue)
    ? values.attributeValue
    : [];

  const addValue = () => {
    const value = newValue.trim();

    if (!value) return;

    const exists = listValues.some((item) => {
      const current = typeof item === "string" ? item : item?.value;

      return String(current ?? "").toLowerCase() === value.toLowerCase();
    });

    if (exists) return;

    setFieldValue("attributeValue", [...listValues, { label: value, value }]);

    setNewValue("");
  };

  const removeValue = (index) => {
    setFieldValue(
      "attributeValue",
      listValues.filter((_, i) => i !== index),
    );
    setFieldValue("defaultValue", "");
  };

  const handleListTypeChange = (selected) => {
    setFieldValue("listType", selected);
    setFieldValue("defaultValue", "");
    if (selected?.value === "open") setFieldValue("attributeValue", []);
    setNewValue("");
  };

  return (
    <>
      <style>{styles}</style>

      <div className="list-configuration">
        <div className="lc-header">
          <h5 className="lc-title">
            <span className="lc-icon">
              <ListIcon />
            </span>
            List Configuration
          </h5>

          <span className="lc-count">
            {listType === "open" ? 0 : listValues.length} values
          </span>
        </div>

        <div className="lc-grid">
          {/* List Type */}
          <div className="lc-field">
            <label className="ms-0 rv-form-label">
              List Type <span className="text-danger">*</span>
            </label>

            <label className="d-block ms-0 rv-sub-label rv-label-muted">
              Select how users can select values
            </label>

            <SelectField
              value={selectedListType}
              placeholder={" "}
              handleChange={handleListTypeChange}
              options={LIST_TYPE_OPTIONS}
              isRVStyle={true}
            />
          </div>

          {/* List Values */}
          <div className="lc-field">
            <label className="ms-0 rv-form-label">
              List Values
              {listType !== "open" && <span className="text-danger"> *</span>}
            </label>

            <label className="d-block ms-0 rv-sub-label rv-label-muted">
              Add options for this attribute
            </label>

            <div className="lc-editor">
              {listType === "open" ? (
                <div className="lc-empty">
                  <div>No predefined values required</div>
                  <small>
                    Users can enter a custom value when using this attribute.
                  </small>
                </div>
              ) : (
                <>
                  <div className="lc-values">
                    {listValues.map((item, index) => {
                      const label =
                        typeof item === "string"
                          ? item
                          : (item?.label ?? item?.value ?? "");

                      return (
                        <div className="lc-value-row" key={`${label}-${index}`}>
                          <span style={{ fontWeight: "normal" }}>{label}</span>

                          <button
                            type="button"
                            className="lc-remove"
                            aria-label={`Remove ${label}`}
                            onClick={() => removeValue(index)}
                          >
                            &times;
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <div className="lc-add-row">
                    <input
                      className="lc-input"
                      type="text"
                      value={newValue}
                      placeholder="Add a value and press Enter"
                      onChange={(event) => setNewValue(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          addValue();
                        }
                      }}
                    />

                    <button
                      type="button"
                      className="lc-add-button"
                      aria-label="Add value"
                      onClick={addValue}
                    >
                      +
                    </button>
                  </div>
                </>
              )}
            </div>

            {touched?.attributeValue && errors?.attributeValue && (
              <div className="lc-error">{errors.attributeValue}</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}